import axios from 'axios'
import AuthService from './auth-header'
import enhancedInvoiceRevisionService from './enhanced-invoice-revision-service'
import enhancedInvoiceService from './enhanced-invoice-service'
import enhancedInvoiceNotificationService from './enhanced-invoice-notification-service'
import {
  buildInvoiceTimeline,
  buildInvoiceTimelineMeta,
} from '@/utils/invoice-timeline-builder'
import { resolveInvoiceApiKey } from '@/utils/invoice-api-key'

let API_URL = ''
if (process.env.NODE_ENV === "production") {
  API_URL = 'https://ats.sattvahuman.in/api/'
} else {
  API_URL = 'http://192.168.0.30:5000/api/'
}

/**
 * Dedicated GET/POST /invoiceTimeline is optional and currently 404 on production
 * (see backend-handoff/INVOICE_BACKEND_SPEC.md). Default: never call it — use aggregated revision /
 * documents / notifications history instead.
 *
 * Set VUE_APP_INVOICE_TIMELINE_API=true when the backend route ships; then the
 * service probes once per session and caches "unavailable" on 404/405/501 so
 * Network is not flooded with repeat misses.
 */
const PROBE_DEDICATED_TIMELINE = String(process.env.VUE_APP_INVOICE_TIMELINE_API || '').toLowerCase() === 'true'

const SESSION_GET_UNAVAILABLE = 'invoiceTimeline.getUnavailable'
const SESSION_POST_UNAVAILABLE = 'invoiceTimeline.postUnavailable'

let dedicatedGetUnavailable = !PROBE_DEDICATED_TIMELINE || readSessionFlag(SESSION_GET_UNAVAILABLE)
let dedicatedPostUnavailable = !PROBE_DEDICATED_TIMELINE || readSessionFlag(SESSION_POST_UNAVAILABLE)

function readSessionFlag(key) {
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key) === '1') {
      return true
    }
  } catch (_) { /* private mode / SSR */ }
  return false
}

function markSessionFlag(key) {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, '1')
    }
  } catch (_) { /* ignore */ }
}

function markDedicatedGetUnavailable() {
  dedicatedGetUnavailable = true
  markSessionFlag(SESSION_GET_UNAVAILABLE)
}

function markDedicatedPostUnavailable() {
  dedicatedPostUnavailable = true
  markSessionFlag(SESSION_POST_UNAVAILABLE)
}

function isOk(res) {
  if (!res) return false
  const st = res.status
  return st === 'OK' || st === 200 || (typeof st === 'string' && String(st).toLowerCase() === 'ok')
}

function extractTimelineArray(res) {
  if (!res) return []
  if (Array.isArray(res)) return res
  if (Array.isArray(res.data)) return res.data
  if (Array.isArray(res.timeline)) return res.timeline
  if (res.data && Array.isArray(res.data.timeline)) return res.data.timeline
  return []
}

function extractRevisionList(res) {
  // Always go through the shared normalizer so Postgres columns
  // (createdAt, initiated_by, change_type, document_urls, …) map correctly.
  const { rows } = enhancedInvoiceRevisionService.extractRevisionRows(res)
  return rows
}

function extractDocList(res) {
  if (!isOk(res)) return []
  if (Array.isArray(res.data)) return res.data
  if (Array.isArray(res.documents)) return res.documents
  if (res.data && Array.isArray(res.data.documents)) return res.data.documents
  return []
}

function isMissingRouteStatus(code) {
  return code === 404 || code === 405 || code === 501
}

class InvoiceTimelineService {
  /**
   * Timeline for revise / view UI.
   * By default uses aggregated sources only (no /invoiceTimeline/:id request).
   * With VUE_APP_INVOICE_TIMELINE_API=true, probes dedicated GET once; on 404
   * caches unavailable for the browser session and falls back to aggregate.
   *
   * @param {string|object} invoiceId
   * @param {object|null} invoiceRecord
   * @param {{ revisions?: array, revisionSource?: string }} [options] — optional preloaded revision history
   */
  async getInvoiceTimeline(invoiceId, invoiceRecord = null, options = {}) {
    const resolvedId = resolveInvoiceApiKey(invoiceRecord) || resolveInvoiceApiKey(invoiceId)
    if (!resolvedId) {
      return {
        status: 'OK',
        data: [],
        message: 'Invoice id is required.',
        _source: 'empty',
        _meta: buildInvoiceTimelineMeta({ invoice: invoiceRecord, revisions: [] }),
      }
    }

    if (!dedicatedGetUnavailable) {
      try {
        const res = await axios.get(API_URL + 'invoiceTimeline/' + encodeURIComponent(resolvedId), {
          headers: AuthService.authHeader(),
          validateStatus: (status) => (status >= 200 && status < 300) || isMissingRouteStatus(status),
        })

        if (isMissingRouteStatus(res.status)) {
          markDedicatedGetUnavailable()
        } else {
          const items = extractTimelineArray(res.data)
          if (items.length) {
            const meta = buildInvoiceTimelineMeta({
              invoice: invoiceRecord,
              revisions: options.revisions,
            })
            // Dedicated API may already include Revised rows; still flag status gap
            // if invoice is Revised and response has no Revised-titled entries.
            const hasRevised = items.some((e) => /—\s*Revised/i.test(String((e && e.title) || ''))
              || String((e && e.id) || '').startsWith('revision-'))
            return {
              status: 'OK',
              data: items,
              _source: 'invoiceTimeline-api',
              _meta: {
                ...meta,
                revisionHistoryGap: meta.statusIsRevised && !hasRevised && !(meta.revisionCount > 0),
              },
            }
          }
        }
      } catch (_) {
        // Transient network error — soft-fail into aggregate; do not mark unavailable.
      }
    }

    return this.buildAggregatedTimeline(resolvedId, invoiceRecord, options)
  }

  async buildAggregatedTimeline(resolvedId, invoiceRecord = null, options = {}) {
    const preloaded = options && Array.isArray(options.revisions) ? options.revisions : null
    try {
      const [revisionsRes, signedRes, docsRes, notifRes] = await Promise.all([
        preloaded
          ? Promise.resolve(null)
          : enhancedInvoiceRevisionService.getRevisionHistory(resolvedId).catch(() => null),
        enhancedInvoiceService.listSignedInvoices(resolvedId).catch(() => null),
        enhancedInvoiceService.getDocuments(resolvedId).catch(() => null),
        enhancedInvoiceNotificationService.getNotificationHistory(resolvedId).catch(() => null),
      ])

      const revisions = preloaded != null
        ? preloaded
        : extractRevisionList(revisionsRes)
      const revisionSource = preloaded != null
        ? (options.revisionSource || 'preloaded')
        : (revisionsRes && revisionsRes._source) || (revisions.length ? 'revision-history-api' : 'empty')

      const timeline = buildInvoiceTimeline({
        invoice: invoiceRecord,
        revisions,
        signedInvoices: isOk(signedRes) && Array.isArray(signedRes.data) ? signedRes.data : [],
        documents: extractDocList(docsRes),
        notifications: extractDocList(notifRes),
      })

      const meta = {
        ...buildInvoiceTimelineMeta({ invoice: invoiceRecord, revisions }),
        revisionSource,
      }

      return { status: 'OK', data: timeline, _source: 'aggregated', _meta: meta }
    } catch (_) {
      const timeline = buildInvoiceTimeline({ invoice: invoiceRecord })
      return {
        status: 'OK',
        data: timeline,
        _source: 'invoice-only',
        _meta: buildInvoiceTimelineMeta({ invoice: invoiceRecord, revisions: [] }),
      }
    }
  }

  /** Future: POST /invoiceTimeline — skipped unless VUE_APP_INVOICE_TIMELINE_API=true. */
  async create(data) {
    if (dedicatedPostUnavailable) {
      return { status: 'OK', data: null, _skipped: true }
    }
    try {
      const res = await axios.post(API_URL + 'invoiceTimeline', data, {
        headers: AuthService.authHeader(),
        validateStatus: (status) => (status >= 200 && status < 300) || isMissingRouteStatus(status),
      })
      if (isMissingRouteStatus(res.status)) {
        markDedicatedPostUnavailable()
        return { status: 'OK', data: null, _skipped: true }
      }
      return res.data
    } catch (_) {
      return { status: 'OK', data: null, _skipped: true }
    }
  }
}

export default new InvoiceTimelineService()
